import React, { useState, useEffect, useCallback } from 'react';
import { isWeekend, toLocalDateStr } from '../utils/hkHolidays';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Alert, CircularProgress,
  Box, Autocomplete, Typography,
} from '@mui/material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import MultiDatePicker from './MultiDatePicker';

interface LeaveType {
  id: string;
  name: string;
  code: string;
  isPaid: boolean;
}

interface AddLeaveRequestModalProps {
  open: boolean;
  onClose: () => void;
  onLeaveRequestAdded: () => void;
}

const AddLeaveRequestModal: React.FC<AddLeaveRequestModalProps> = ({
  open,
  onClose,
  onLeaveRequestAdded,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    leaveTypeId: '',
    selectedDates: [] as string[],
    reason: '',
  });

  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);
  const [holidaySet, setHolidaySet] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    try {
      setDataLoading(true);
      const [types] = await Promise.all([
        apiService.getLeaveTypes(),
        loadHolidays(),
      ]);
      setLeaveTypes(types);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setDataLoading(false);
    }
  }, []);

  const loadHolidays = async () => {
    try {
      // Resolve employee's country to a countryId
      let countryId: number | undefined;
      const employeeCountry = user?.employee?.country;
      if (employeeCountry) {
        try {
          const countries = await apiService.getCountries();
          const match = countries.find(
            (c: any) =>
              c.name.toLowerCase() === employeeCountry.toLowerCase() ||
              c.code.toLowerCase() === employeeCountry.toLowerCase()
          );
          if (match) countryId = match.id;
        } catch {
          // proceed without country filter
        }
      }

      const currentYear = new Date().getFullYear();
      const years = [currentYear, currentYear + 1];
      const results = await Promise.all(
        years.map(y => apiService.getPublicHolidays(y, countryId))
      );
      const set = new Set<string>();
      results.flat().forEach((h: any) => {
        set.add(String(h.date).split('T')[0]);
      });
      setHolidaySet(set);
    } catch {
      // holidays unavailable, count without them
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const countWorkingDays = (startDateStr: string, endDateStr: string): number => {
    const cur = new Date(startDateStr + 'T00:00:00');
    const end = new Date(endDateStr + 'T00:00:00');
    let count = 0;
    while (cur <= end) {
      const dateStr = toLocalDateStr(cur);
      if (!isWeekend(cur) && !holidaySet.has(dateStr)) {
        count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };


  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!user?.employee?.id) {
        setError('Employee information not found. Please contact your administrator.');
        return;
      }

      const sorted = [...formData.selectedDates].sort();
      const requestData = {
        leaveTypeId: formData.leaveTypeId,
        employeeId: user.employee.id,
        startDate: sorted[0],
        endDate: sorted[sorted.length - 1],
        selectedDates: sorted,
        totalDays: sorted.length,
        reason: formData.reason,
      };

      await apiService.submitLeaveRequest(requestData);

      onLeaveRequestAdded();
      onClose();
      setFormData({ leaveTypeId: '', selectedDates: [], reason: '' });
    } catch (err: any) {
      console.error('Error submitting leave request:', err);
      setError(err.response?.data?.message || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.leaveTypeId && formData.selectedDates.length > 0 && formData.reason;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Leave</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dataLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <Autocomplete
                options={leaveTypes}
                getOptionLabel={(option) => option.name}
                value={leaveTypes.find(t => t.id === formData.leaveTypeId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('leaveTypeId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Leave Type"
                    required
                    helperText="Select leave type"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Select dates (tap individual days — non-consecutive is fine)
              </Typography>
              <MultiDatePicker
                selectedDates={formData.selectedDates}
                onChange={(dates) => handleInputChange('selectedDates', dates)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                placeholder="Please provide a reason for your leave request"
                required
              />
            </Grid>
            {formData.selectedDates.length > 0 && (
              <Grid size={12}>
                <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.light' }}>
                  <Typography variant="body2"><strong>{formData.selectedDates.length} day{formData.selectedDates.length > 1 ? 's' : ''} selected</strong></Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid || dataLoading}
        >
          {loading ? <CircularProgress size={20} /> : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLeaveRequestModal;
